import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

const worldQuestions = [
  {
    category: 'География',
    questions: [
      'Какие королевства или империи существуют?',
      'Где находятся опасные места, куда никто не ходит?',
      'Какие реки, горы, леса важны для мира?',
      'Есть ли подземные царства или затерянные города?',
    ],
  },
  {
    category: 'История',
    questions: [
      'Какие войны изменили мир?',
      'Какие древние цивилизации пали и почему?',
      'Кто были великие герои прошлого?',
      'Какие тайны древности ещё не раскрыты?',
    ],
  },
  {
    category: 'Религия и магия',
    questions: [
      'Каким богам поклоняются?',
      'Как работает магия — дар богов или наука?',
      'Есть ли запретная магия?',
      'Какие религиозные конфликты существуют?',
    ],
  },
  {
    category: 'Общество',
    questions: [
      'Кто правит — короли, совет, маги?',
      'Как относятся к нелюдям — эльфам, гномам?',
      'Есть ли рабство или крепостное право?',
      'Какие гильдии и организации влиятельны?',
    ],
  },
  {
    category: 'Угрозы',
    questions: [
      'Какое зло пробуждается в мире?',
      'Какие монстры представляют угрозу?',
      'Есть ли тёмные культы?',
      'Какие соседние народы враждебны?',
    ],
  },
];

const cartographyTips = [
  'Начните с крупных мазков — континенты, моря, горные хребты',
  'Оставляйте белые пятна — неисследованные территории',
  'Размещайте цивилизации у рек и на побережьях',
  'Горы создают границы и определяют погоду',
  'Леса — это барьеры и места опасности',
  'Руины древних цивилизаций манят приключенцев',
  'У каждого места должна быть своя история',
];

export function WorldBuildingPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/gm-tools');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Создание мира</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20"
        >
          <p className="text-amber-200 text-sm">
            🌍 В Dungeon World мир создаётся совместно с игроками. Задавайте вопросы и используйте их ответы!
          </p>
        </motion.div>

        {/* World Questions */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            Вопросы для создания мира
          </motion.h2>

          <div className="space-y-4">
            {worldQuestions.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + 0.05 * index }}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <h3 className="font-medium text-white mb-3">{category.category}</h3>
                <ul className="space-y-2">
                  {category.questions.map((question, i) => (
                    <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                      <span className="text-amber-400">?</span>
                      {question}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Cartography Tips */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            🗺️ Советы по картографии
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
          >
            <ul className="space-y-3">
              {cartographyTips.map((tip, index) => (
                <li key={index} className="text-gray-300 text-sm flex items-start gap-2">
                  <span className="text-amber-400">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* Collaborative Worldbuilding */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h2 className="text-lg font-bold text-amber-400 mb-3">Совместное создание</h2>
          <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 space-y-3">
            <p className="text-gray-300 text-sm">
              <span className="text-white">Принцип:</span> Задавай вопросы игрокам и используй их ответы.
            </p>
            <p className="text-gray-300 text-sm">
              <span className="text-white">Пример:</span> "Рейнольд, ты уже бывал в этом городе. Что здесь случилось в прошлый раз?"
            </p>
            <p className="text-gray-300 text-sm">
              <span className="text-white">Результат:</span> Игрок создаёт часть мира, а вы получаете материал для приключений.
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
